import { useCallback, useMemo } from 'react';

import type { GitHubIntegration } from './useGitHubIntegration';

/**
 * A stored selection. `'all'` is sticky — it keeps following the private repo list as repos are
 * added or removed, where a frozen key list would not. Only the browser-local surfaces ever hold
 * it; writes are always explicit keys, since that is all the picker produces.
 */
export type RepoSelection = string[] | 'all';

export interface RepoSelectionStorage {
  value: RepoSelection;
  onChange: (next: string[]) => void;
}

export interface GitHubRepoSelection {
  /** Selected keys, clamped to repos that are actually selectable. */
  selectedKeys: string[];
  /** The same selection as repo names, which is what the cleanup and investigation APIs take. */
  selectedNames: string[];
  isEmpty: boolean;
  setSelected: (keys: string[]) => void;
  /** Resolve persisted repo names back to keys, for seeding from a saved config. */
  toKeys: (names: string[]) => string[];
  /** Spread onto `<GitHubRepoSelector />`. */
  selectorProps: {
    repos: GitHubIntegration['allRepos'];
    selectedKeys: string[];
    onSelectionChange: (keys: string[]) => void;
  };
}

/**
 * Owns the repo selection that goes with a `useGitHubIntegration` status.
 *
 * Public repos can't be selected, so a stored selection has to be clamped against the private list
 * every render — otherwise a repo that went public, or an install that lost access to one, leaves a
 * key selected that the picker won't show. Every call site was doing that clamp and the key/name
 * round trip by hand.
 *
 * Storage stays with the caller, because the four surfaces disagree about it: two share a
 * localStorage slot, two persist names on a server-side config.
 */
export function useGitHubRepoSelection({
  integration,
  storage,
}: {
  integration: GitHubIntegration;
  storage: RepoSelectionStorage;
}): GitHubRepoSelection {
  const { allRepos, privateRepos, privateRepoKeys } = integration;
  const { value, onChange } = storage;

  const selectedKeys = useMemo(
    () => (value === 'all' ? privateRepoKeys : value.filter((key) => privateRepoKeys.includes(key))),
    [value, privateRepoKeys],
  );

  const selectedNames = useMemo(
    () => privateRepos.filter((repo) => selectedKeys.includes(repo.key)).map((repo) => repo.name),
    [privateRepos, selectedKeys],
  );

  const toKeys = useCallback(
    (names: string[]) => privateRepos.filter((repo) => names.includes(repo.name)).map((repo) => repo.key),
    [privateRepos],
  );

  const setSelected = useCallback((keys: string[]) => onChange(keys), [onChange]);

  return {
    selectedKeys,
    selectedNames,
    isEmpty: selectedKeys.length === 0,
    setSelected,
    toKeys,
    selectorProps: { repos: allRepos, selectedKeys, onSelectionChange: setSelected },
  };
}
